// src/PerformanceList.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const PERF_API = 'https://2af2e5kjj9.execute-api.us-east-1.amazonaws.com/prod/performance';

// simple formatters
const fmtPct   = (v) => (Number.isFinite(v) ? `${v.toFixed(1)} %` : '—');
const fmtPts   = (v) => (Number.isFinite(v) ? `${v.toFixed(0)} pts` : '—');
const belowOn  = (v) => Number(v) < 95;
const belowSaf = (v) => Number(v) < 850;

async function authHeaders() {
  const { tokens } = await fetchAuthSession();
  const jwt =
    tokens?.idToken?.toString?.() ??
    tokens?.accessToken?.toString?.();
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

export default function PerformanceList() {
  const [rows, setRows] = useState([]);               // [{ Name, onTime, safety }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ key: 'Name', dir: 'asc' }); // 'Name' | 'onTime' | 'safety'
  const [weekStart, setWeekStart] = useState('');      // optional MM/DD/YYYY
  const [weekEnd, setWeekEnd] = useState('');          // optional MM/DD/YYYY

  const fetchScores = useCallback(async () => {
    const ctrl = new AbortController();
    setLoading(true);
    setError('');
    try {
      const url = new URL(PERF_API);
      if (weekStart.trim()) url.searchParams.set('weekStart', weekStart.trim());
      if (weekEnd.trim())   url.searchParams.set('weekEnd',   weekEnd.trim());

      const res = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
          ...(await authHeaders()),
        },
        signal: ctrl.signal,
      });

      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : null; } catch { data = null; }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error('Unauthorized. Please sign in to view performance.');
        }
        throw new Error(data?.message || `GET /performance -> ${res.status}`);
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(String(e?.message || e));
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
    // return a no-op cancel for callers if needed
    return () => ctrl.abort();
  }, [weekStart, weekEnd]);

  useEffect(() => { fetchScores(); }, [fetchScores]);

  const requestSort = (key) => {
    setSort((prev) => {
      const dir = prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc';
      return { key, dir };
    });
  };

  const filteredSorted = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = [...rows];

    if (q) {
      list = list.filter(r =>
        (r.Name || '').toString().toLowerCase().includes(q)
        || String(r.onTime ?? '').toLowerCase().includes(q)
        || String(r.safety ?? '').toLowerCase().includes(q)
      );
    }

    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      if (key === 'Name') {
        const an = (a.Name || '').toString().toLowerCase();
        const bn = (b.Name || '').toString().toLowerCase();
        if (an < bn) return -1 * mul;
        if (an > bn) return  1 * mul;
        return 0;
      }
      // numeric keys
      const av = Number(a[key] ?? -Infinity);
      const bv = Number(b[key] ?? -Infinity);
      if (av < bv) return -1 * mul;
      if (av > bv) return  1 * mul;
      return 0;
    });

    return list;
  }, [rows, searchTerm, sort]);

  return (
    <div className="container listDiv">
      <div className="search-and-add-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search name or score…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Optional week filters (MM/DD/YYYY) */}
        <input
          type="text"
          placeholder="Week start (MM/DD/YYYY)"
          style={{ width: 160 }}
          value={weekStart}
          onChange={(e) => setWeekStart(e.target.value)}
        />
        <input
          type="text"
          placeholder="Week end (MM/DD/YYYY)"
          style={{ width: 160 }}
          value={weekEnd}
          onChange={(e) => setWeekEnd(e.target.value)}
        />
        <button onClick={fetchScores} disabled={loading}>Apply / Refresh</button>
        <button
          onClick={() => { setWeekStart(''); setWeekEnd(''); setSearchTerm(''); fetchScores(); }}
          disabled={loading}
        >
          Clear
        </button>
      </div>

      {error && <div style={{ color: 'crimson', margin: '8px 0' }}>{error}</div>}

      <div className="table-container">
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="styleCol2" onClick={() => requestSort('Name')}>Name</th>
                <th className="styleCol1" onClick={() => requestSort('onTime')}>On-time %</th>
                <th className="styleCol1" onClick={() => requestSort('safety')}>Safety</th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {filteredSorted.map((r, idx) => (
                <tr key={`${r.Name}-${idx}`}>
                  <td className="styleCol2">{r.Name || 'Unknown'}</td>
                  <td className="styleCol1">
                    <span style={{ color: belowOn(r.onTime) ? '#C80036' : 'inherit', fontWeight: 600 }}>
                      {fmtPct(Number(r.onTime))}
                    </span>
                  </td>
                  <td className="styleCol1">
                    <span style={{ color: belowSaf(r.safety) ? '#C80036' : 'inherit', fontWeight: 600 }}>
                      {fmtPts(Number(r.safety))}
                    </span>
                  </td>
                </tr>
              ))}
              {!filteredSorted.length && (
                <tr><td colSpan={3} style={{ color: '#666' }}>No matches</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
